import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Routes } from '../../config/routes';
import { ApplicationHttpClient } from '../../components/shared/http/application-http-client';

/**
 * SD-9732. How the browser saw a Financial Connections session end.
 *
 * These are FACTS the client can state, never verdicts. `empty` is deliberately not "abandoned":
 * today a closed window and a completed manual entry both resolve with zero accounts and no
 * error, and the server settles which it was from the session object (SD-10156). `cancelled` is
 * reserved for Stripe's cancellation-as-error preview, which reports a close as an error code
 * instead of a succeeded session with no accounts; nothing produces it until that is enabled.
 */
export const FcSessionOutcomes = {
    /** At least one account came back. */
    COMPLETED: 'completed',
    /** Succeeded with zero accounts and no error — the ambiguous shape this ticket exists to classify. */
    EMPTY: 'empty',
    /** Reserved: Stripe's cancellation-as-error preview. Not produced until it is on. */
    CANCELLED: 'cancelled',
    /** Stripe returned an error, or the popup promise rejected. */
    ERROR: 'error',
} as const;

export type FcSessionOutcome = (typeof FcSessionOutcomes)[keyof typeof FcSessionOutcomes];

/**
 * SD-9732. Event names this client records around the popup.
 *
 * Producers today: `error` (the `code` off a returned StripeError) and the two relink verdicts
 * Stripe already states on the session (`relink_result`). Stripe's client-side auth-flow events
 * preview (institution_selected, accounts_selected, authorization_failed, …) lands in this same
 * list once it is enabled for the platform account — the server accepts any name, so no shape
 * change is needed on either side when it does.
 */
export const FcAuthFlowEvents = {
    ERROR: 'error',
    /** SD-9989 relink: Stripe confirmed the pinned account was re-authorized (`relink_result.account`). */
    RELINK_SUCCEEDED: 'relink_succeeded',
    /** SD-9989 relink: Stripe stated why the relink failed (`relink_result.failure_reason` rides `code`). */
    RELINK_FAILED: 'relink_failed',
} as const;

/**
 * One observation during the popup, stamped as a monotonic offset from when it opened.
 */
export interface FcAuthFlowEvent {
    event: string;
    /** Stripe's error/event code when it carries one; null otherwise. */
    code: string | null;
    /** Milliseconds since the popup opened (performance.now() delta, so clock changes cannot skew it). */
    atMs: number;
}

/**
 * SD-9732. What the browser observed around a Financial Connections popup.
 *
 * `durationMs` was the whole signal when this shipped as a stopwatch: Stripe reported nothing
 * about how a session ended, so a customer who closed the popup and one who completed the flow
 * and received nothing were identical to us. `outcome` and `events` carry what we can state
 * today and are the landing zone for Stripe's auth-flow events preview.
 */
export interface FcSessionOutcomeRequest {
    sessionId: string;
    durationMs: number;
    accountsReturned: number;
    reportedError: boolean;
    outcome: FcSessionOutcome;
    events: FcAuthFlowEvent[];
}

/**
 * SD-9732. Reports how a Financial Connections session ended.
 *
 * Exists because Stripe provides NO cancellation or abandonment signal: its
 * `FinancialConnectionsSessionResult` is a two-arm union with no cancellation variant, and the
 * session object carries no status (verified against @stripe/stripe-js 7.9.0). So SD-9590's 128
 * no-return sessions cannot be split into "closed the popup" and "completed and got nothing" —
 * the split Stripe's own triage asks for — from anything Stripe sends us.
 *
 * What we DO control is the clock around the popup. A session resolving in seconds reads as an
 * abandon; one resolving after minutes with zero accounts was a real attempt that returned nothing.
 *
 * Modelled on TimeoutLoggingService, which is the only surviving UI -> server event emitter in this
 * repo: `ClientErrorLoggingService` and the Better Stack client sink were removed (SD-7666/7717) and
 * UI Sentry is dead config, so there is no generic client sink to reuse.
 */
@Injectable({
    providedIn: 'root',
})
export class FinancialConnectionsTelemetryService {
    constructor(private _http: ApplicationHttpClient) {}

    /**
     * Fire-and-forget. Telemetry must never affect the journey it measures, so every failure is
     * swallowed — the customer's bank link does not fail because a diagnostic POST did.
     *
     * Callers may ignore the returned Observable, but it is COLD: nothing is sent until something
     * subscribes. `reportSessionOutcome(...).subscribe()` is the intended call shape.
     */
    public reportSessionOutcome(
        organizationId: number,
        request: FcSessionOutcomeRequest,
    ): Observable<void> {
        // The session id rides the route (the same shape as the SD-10156 `resolve` and SD-10187
        // `inspect` siblings) and is stripped from the body so it is not stated twice.
        const { sessionId, ...body } = request;
        const url = Routes.MakeLegFiCoreUrl(
            Routes.TreasuryModule.FinancialConnectionsSessionOutcome(organizationId, sessionId),
        );

        return this._http.post<void>(url, JSON.stringify(body)).pipe(
            catchError((error) => {
                console.error('Failed to report Financial Connections session outcome:', error);
                return of(void 0);
            }),
        );
    }
}
